import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import Api from "../../utils/Api";
import { useAuth } from "../../context/AuthContext";
import { Link } from "react-router-dom";

const EmailTempTagsModal = ({ isModal = true }) => {
  const [isOpen, setIsOpen] = useState(!isModal); // open by default if static mode
  const [showToast, setShowToast] = useState(false);
  const { getToken } = useAuth();
  const api = useMemo(() => Api(() => getToken()), [getToken]);
  const [tempTags, setTempTags] = useState([]);
  const hasLoadedOnceRef = useRef(false);
  const modalRef = useRef(null);

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 500);
  }, []);

  // Fetch data when first opened (modal) or on first render (static)
  useEffect(() => {
    if (!isOpen || hasLoadedOnceRef.current) return;
    let isActive = true;
    (async () => {
      try {
        const response = await api.call(
          "template-body-tags",
          "GET",
          null,
          true,
          false
        );
        if (!isActive) return;
        if (response?.status === 200) {
          setTempTags(response.data?.tempBodyTags || []);
        }
      } catch (e) {
        // silently ignore
      } finally {
        hasLoadedOnceRef.current = true;
      }
    })();
    return () => {
      isActive = false;
    };
  }, [isOpen, api]);

  // Close modal on outside click (only if modal mode)
  useEffect(() => {
    if (!isModal) return;
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, isModal]);

  const content = (
    <div
      className={`${
        isModal
          ? "bg-white rounded-lg shadow-lg w-full max-w-3xl mx-auto overflow-hidden"
          : "bg-white border rounded-lg shadow-sm p-4"
      }`}
      ref={isModal ? modalRef : null}
    >
      {/* Header */}
      <div className={`flex justify-between items-center ${isModal ? "border-b p-4" : "mb-4"}`}>
        <h3 className="text-lg sm:text-xl font-semibold">Tags</h3>
        {isModal && (
          <button
            className="text-gray-500 hover:text-gray-700 text-lg sm:text-xl"
            onClick={() => setIsOpen(false)}
          >
            ✕
          </button>
        )}
      </div>

      {/* Body */}
      <div className="p-1 max-h-[80vh] md:max-h-[85vh] lg:max-h-[70vh] overflow-y-auto scrollbar-thin">
        <div className="bg-blue-50 text-blue-900 p-4 sm:p-6 rounded-lg mb-4 border-l-4 border-blue-500 text-sm xs:text-base leading-relaxed">
          <strong className="font-semibold block mb-1">NOTE:</strong>
          <p>
            Use these tags to make your template dynamic.
            When rendered, each tag is automatically replaced with real data from your records (e.g., estimate, job, customer details).
          </p>

          <div className="my-3">
            <strong className="font-semibold block mb-1">Example:</strong>
            <ul className="list-disc list-inside space-y-2">
              <li>
                Writing{" "}
                <code className="text-pink-600 bg-gray-100 px-1 py-0.5 rounded break-words text-xs sm:text-sm">
                  {"{CustomerContact:FirstName}"}
                </code>{" "}
                will display the customer first name.
              </li>
              
            </ul>
          </div>

          <p>
            This lets you build one template that works across all modules without manual edits
          </p>
        </div>
 
        <ul className="divide-y divide-gray-200 border rounded-lg overflow-hidden">
          {tempTags.length > 0 ? (
            tempTags.map((tag, index) => (
              <li
                key={index}
                className="px-4 py-2 bg-white hover:bg-gray-100 cursor-pointer"
                onClick={() => copyToClipboard(tag)}
              >
                <code className="text-pink-500 px-1 rounded">{tag}</code>
              </li>
            ))
          ) : (
            <li className="px-4 py-2 bg-white text-gray-500">No tags available</li>
          )}
        </ul>
      </div>

      {showToast && (
        <div className="fixed inset-0 flex items-center justify-center z-50 opacity-50">
          <div className="bg-black text-white px-4 py-2 rounded shadow-lg">
            Copied!
          </div>
        </div>
      )}

      {/* Footer */}
      {isModal && (
        <div className="flex justify-end p-4 border-t">
          <button
            className="bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded"
            onClick={() => setIsOpen(false)}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {isModal ? (
        <>
          {/* Trigger */}
          <Link
            to="#"
            className="text-blue-500 underline"
            onClick={() => setIsOpen(true)}
          >
            Tags
          </Link>

          {isOpen &&
            createPortal(
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
                {content}
              </div>,
              document.body
            )}
        </>
      ) : (
        content
      )}
    </>
  );
};

export default EmailTempTagsModal;
